import type { TResolution, TResolutionsLimits } from '@streaming/doppio-player';
import { bind } from '@streaming/mouflon-utils';

import { EMetricsEvents } from '@metrics/Metrics.events';
import { IDimensions } from '@metrics/VideoElementMetrics';
import { TMMPEventBus } from '@utils/EventBus';
import { parseCSSTransformString } from '@utils/cssTransformParser';
import { debounce, TDebouncedFunction } from '@utils/debounce';

class UpscaleCalculationService {
  private _containerDimensions: IDimensions | null = null;
  private _devicePixelRatio: number = 1;
  private _videoElement: HTMLVideoElement | null = null;
  private _videoResizeObserver: ResizeObserver | null = null;
  private _userCap: IDimensions | null = null;
  private _resolutions: TResolution[] = [];
  private _userSelectedResolution: string | null = null;
  private readonly _eventBus: TMMPEventBus;
  private _dimencionVideo: IDimensions = { width: 0, height: 0 };
  private _debouncedUpdateContainerDimensions: TDebouncedFunction<() => void>;

  constructor(eventBus: TMMPEventBus) {
    this._eventBus = eventBus;
    this._updateDevicePixelRatio();
    this._debouncedUpdateContainerDimensions = debounce(this._updateContainerDimensions, 100);
  }

  public setVideoElement(video: HTMLVideoElement | null): void {
    this._videoResizeObserver?.disconnect();
    this._videoResizeObserver = null;

    this._debouncedUpdateContainerDimensions.cancel();

    if (this._videoElement) {
      this._videoElement.removeEventListener('timeupdate', this._onVideoTimeUpdate);
    }

    this._videoElement = video;

    if (!video) {
      this._containerDimensions = null;
      this._dimencionVideo = {
        width: 0,
        height: 0,
      };
      return;
    }

    this._onVideoElementResized();

    video.addEventListener('timeupdate', this._onVideoTimeUpdate);

    if ('ResizeObserver' in globalThis) {
      this._videoResizeObserver = new ResizeObserver(() => {
        this._onVideoElementResized();
      });
      this._videoResizeObserver.observe(video);
    }
  }

  public setResolutions(resolutions: TResolution[]): void {
    this._resolutions = [...resolutions].sort((a, b) => b.height - a.height);
    this._recalculateUserCap();
  }

  public setUserSelectedResolution(userSelectedResolution: string | null): void {
    this._userSelectedResolution = userSelectedResolution;
    this._recalculateUserCap();
  }
  public getUpscaleFactor(
    videoWidth: number,
    videoHeight: number,
    maxQualityDimension?: number,
  ): number | null {
    if (!this._containerDimensions || !videoWidth || !videoHeight) {
      return null;
    }

    const { width, height } = this._containerDimensions;

    if (width < 50 || height < 50) {
      return null;
    }

    let videoDisplayWidth: number = width;
    let videoDisplayHeight: number = height;

    if (!videoWidth || !videoHeight) {
      return null;
    }

    const videoAspectRatio: number = videoWidth / videoHeight;
    const containerAspectRatio: number = width / height;

    if (videoAspectRatio > containerAspectRatio) {
      videoDisplayHeight = width / videoAspectRatio;
    } else {
      videoDisplayWidth = height * videoAspectRatio;
    }

    const isLandscape: boolean = videoDisplayWidth >= videoDisplayHeight;

    const containerDim: number = isLandscape ? videoDisplayWidth : videoDisplayHeight;
    const realContainerDim: number = containerDim * this._devicePixelRatio;

    const userCapDimension: number = this._userCap
      ? isLandscape
        ? this._userCap.width
        : this._userCap.height
      : realContainerDim;
    const effectiveContainerDimension: number = maxQualityDimension
      ? Math.min(realContainerDim, maxQualityDimension, userCapDimension)
      : Math.min(realContainerDim, userCapDimension);

    const videoDimension: number = isLandscape ? videoWidth : videoHeight;
    const rawUpscaleFactor: number = effectiveContainerDimension / videoDimension - 1;
    return Math.max(rawUpscaleFactor, 0);
  }

  public getCurrentUpscaleFactor(maxQualityDimension?: number): number | null {
    if (!this._videoElement) {
      return null;
    }

    const { videoWidth, videoHeight } = this._videoElement;
    if (!videoWidth || !videoHeight) {
      return null;
    }

    return this.getUpscaleFactor(videoWidth, videoHeight, maxQualityDimension);
  }

  public getMaxAllowedResolution(minUpscaleFactor: number): TResolutionsLimits | null {
    if (!this._containerDimensions || !this._resolutions.length || minUpscaleFactor < 0) {
      return null;
    }
    const { width, height } = this._containerDimensions;

    const isLandscape: boolean =
      this._containerDimensions.width >= this._containerDimensions.height;
    if (width < 50 || height < 50) {
      return null;
    }

    const targetResolution: TResolution =
      [...this._resolutions].reverse().find((resolution) => {
        const upscaleFactor: number | null = this.getUpscaleFactor(
          resolution.width,
          resolution.height,
        );
        return upscaleFactor !== null && upscaleFactor < minUpscaleFactor;
      }) || this._resolutions[0];

    return isLandscape ? { width: targetResolution.width } : { height: targetResolution.height };
  }

  public getContainerDimensions(): IDimensions | null {
    return this._containerDimensions;
  }

  public getDevicePixelRatio(): number {
    return this._devicePixelRatio;
  }

  public getVideoElementData(): {
    containerDimensions: IDimensions | null;
    devicePixelRatio: number;
  } {
    return {
      containerDimensions: this._containerDimensions,
      devicePixelRatio: this._devicePixelRatio,
    };
  }

  private _recalculateUserCap(): void {
    if (!this._userSelectedResolution || !this._resolutions.length) {
      this._userCap = null;
    } else {
      const selectedResolution: TResolution | undefined = this._resolutions.find(
        ({ name }) => name === this._userSelectedResolution,
      );

      if (selectedResolution) {
        this._userCap = {
          width: selectedResolution.width,
          height: selectedResolution.height,
        };
      } else {
        this._userCap = null;
      }
    }
  }

  private _onVideoElementResized(): void {
    if (!this._videoElement) {
      return;
    }

    this._debouncedUpdateContainerDimensions();
  }

  private _updateContainerDimensions(): void {
    if (!this._videoElement) {
      return;
    }

    if (
      this._videoElement.style.width?.includes('px') &&
      this._videoElement.style.height?.includes('px') &&
      this._videoElement.style.transform
    ) {
      const width: number = parseFloat(this._videoElement.style.width);
      const height: number = parseFloat(this._videoElement.style.height);

      const scaleFactors: { scaleX: number; scaleY: number } = parseCSSTransformString(
        this._videoElement.style.transform,
      );

      this._containerDimensions = {
        width: width * scaleFactors.scaleX,
        height: height * scaleFactors.scaleY,
      };
    } else {
      const { width, height } = this._videoElement.getBoundingClientRect();
      this._containerDimensions = {
        width,
        height,
      };
    }

    if (this._containerDimensions) {
      this._eventBus.publish(
        EMetricsEvents.VIDEO_ELEMENT_DIMENSIONS_CALCULATED,
        this._containerDimensions,
      );
    }
  }

  private _updateDevicePixelRatio(): void {
    this._devicePixelRatio = globalThis.window?.devicePixelRatio || 1;
  }

  @bind
  private _onVideoTimeUpdate(): void {
    if (!this._videoElement) {
      return;
    }

    const { videoWidth, videoHeight } = this._videoElement;

    if (videoWidth < 50 || videoHeight < 50) {
      return;
    }
    const { width: _lastVideoWidth, height: _lastVideoHeight } = this._dimencionVideo;
    if (videoWidth !== _lastVideoWidth || videoHeight !== _lastVideoHeight) {
      this._dimencionVideo = { width: videoWidth, height: videoHeight };
      this._debouncedUpdateContainerDimensions();

      this._eventBus.publish(
        EMetricsEvents.VIDEO_ELEMENT_DIMENSIONS_CALCULATED,
        this._containerDimensions || { width: 0, height: 0 },
      );
    }
  }
}

export { UpscaleCalculationService };
